import getJSONValue from 'nucleus-widgets/utils/fields/getJSONValue';
import Logger from '@cvent/nucleus-logging';
import { isEmpty } from 'lodash';
import { isOverrideProductFeesValid, isOverrideProductRefundsValid } from '../../regCartPricing';
import { getGuestsOfRegistrant } from './selectors';
import * as currentRegistrantSelectors from '../../selectors/currentRegistrant';
import { CALCULATE_PRICING_FAILURE, CALCULATE_PRICING_SUCCESS } from './actionTypes';
import { ServiceError } from 'event-widgets/utils/errors';
import { promptToTakeOverRegistration } from '../../../initializeMultiTabTracking';

const LOG = new Logger('redux/registrationForm/regCart/pricing');

/**
 * Gets the pricing info needed by the registration service from the user's payment information.
 */
// eslint-disable-next-line complexity
export function getRegCartPricingInfo(regCartPayment, feesEnabled,
  isPlanner = false, taxesEnabled, ignoreTaxes = false, ignoreServiceFee = false, serviceFeeEnabled) {
  const { selectedPaymentMethod } = regCartPayment;
  let pricingInfo;
  const selectedPaymentDetails = regCartPayment.pricingInfo && selectedPaymentMethod
    && getJSONValue(regCartPayment.pricingInfo, selectedPaymentMethod) || { paymentType: 'Offline' };
  if (feesEnabled && selectedPaymentMethod === 'creditCard') {
    const { paymentMethodType, paymentType, ...paymentDetail } = selectedPaymentDetails;
    pricingInfo = {
      paymentMethodType,
      paymentType,
      paymentDetail: {
        [selectedPaymentMethod]: paymentDetail
      }
    };
  } else {
    pricingInfo = selectedPaymentDetails;
  }
  if (isPlanner && feesEnabled && selectedPaymentMethod && pricingInfo.paymentType === 'Offline') {
    pricingInfo = {
      ...pricingInfo,
      date: new Date()
    };
  }
  // Add 'ignoreTaxes' to pricingInfo for planner-reg add/remove taxes price re-calculation
  if (isPlanner && taxesEnabled) {
    pricingInfo = {
      ...pricingInfo,
      ignoreTaxes
    };
  }

  // Add 'ignoreServiceFees' to pricingInfo for planner-reg add/remove serviceFee price re-calculation
  if (isPlanner && serviceFeeEnabled) {
    pricingInfo = {
      ...pricingInfo,
      ignoreServiceFee
    };
  }
  return pricingInfo;
}

export function getPlannerOverriddenPricingInfo(pricingInfo,
  regCartPayment, regCartPricing = null, currentEventRegistrationId,
  isOverrideFeesValid, isOverrideRefundsValid, regCart) {
  const plannerPricingOverride = {
    ...pricingInfo
  };
  if (regCartPricing && regCartPricing.plannerOverriddenProductFees != null &&
    !isEmpty(regCartPricing.plannerOverriddenProductFees) && isOverrideFeesValid) {
    plannerPricingOverride.plannerOverriddenProductFees = {
      ...regCartPricing.plannerOverriddenProductFees
    };
  }
  if (regCartPricing && regCartPricing.plannerOverriddenProductRefunds != null &&
    !isEmpty(regCartPricing.plannerOverriddenProductRefunds) && isOverrideRefundsValid) {
    if (!plannerPricingOverride.plannerOverriddenProductFees) {
      plannerPricingOverride.plannerOverriddenProductFees = {};
    }
    plannerPricingOverride.plannerOverriddenProductFees[currentEventRegistrationId] = {
      ...plannerPricingOverride.plannerOverriddenProductFees[currentEventRegistrationId],
      ...regCartPricing.plannerOverriddenProductRefunds[currentEventRegistrationId]
    };
    // return all guests in the cart no matter registration status
    const guests = getGuestsOfRegistrant(regCart, currentEventRegistrationId, null);
    guests.forEach((guest) => {
      plannerPricingOverride.plannerOverriddenProductFees[guest.eventRegistrationId] = {
        ...plannerPricingOverride.plannerOverriddenProductFees[guest.eventRegistrationId],
        ...regCartPricing.plannerOverriddenProductRefunds[guest.eventRegistrationId]
      };
    });
  }
  return plannerPricingOverride;
}

export function getPricing() : ThunkedAction<Promise<void>> {
  return async (dispatch: Dispatch<*>, getState: GetState) => {
    const { accessToken, registrationForm: { regCart },
      clients: { regCartClient } } = getState();
    try {
      const response = await regCartClient.getRegCartPricing(accessToken, regCart.regCartId);
      LOG.debug('calculatePricing success', response);
      dispatch({ type: CALCULATE_PRICING_SUCCESS, payload: {
        regCartPricing: response.regCartPricing, validationMessages: response.validationMessages
      }
      });
    } catch (error) {
      LOG.info('calculatePricing failed', error);
      dispatch({ type: CALCULATE_PRICING_FAILURE, payload: { error } });
      throw error;
    }
  };
}

/**
 * Calculates the pricing for the current regCart's products. Pricing is only
 * calculated if the regCart is currently in progress.
 */
export function calculatePricing() {
  return async (dispatch, getState) => {
    const { accessToken, event, registrationForm: { regCartPayment, regCart },
      clients: { regCartClient }, userSession: { isPlanner }, regCartPricing
    } = getState();
    if (regCart.status !== 'INPROGRESS') {
      return;
    }
    try {
      const feesEnabled = event.eventFeatureSetup.fees.fees;
      const ignoreTaxes = regCart.ignoreTaxes;
      const ignoreServiceFee = regCart.ignoreServiceFee;
      const taxesEnabled = event.eventFeatureSetup.fees && event.eventFeatureSetup.fees.taxes;
      const serviceFeeEnabled = event.eventFeatureSetup.fees && event.eventFeatureSetup.fees.serviceFees;
      let pricingInfo = getRegCartPricingInfo(regCartPayment, feesEnabled, isPlanner, taxesEnabled, ignoreTaxes,
        ignoreServiceFee, serviceFeeEnabled);
      if (isPlanner && feesEnabled) {
        const currentEventRegistrationId = currentRegistrantSelectors.getEventRegistrationId(getState());
        const isOverrideFeesValid = isOverrideProductFeesValid(getState());
        const isOverrideRefundsValid = isOverrideProductRefundsValid(getState());
        /*
         * during a planner mod if they remove a guest get the ModifcationStartCart so we can
         * save changes if they make any edits in the refund
         */
        const correctRegCart = currentRegistrantSelectors
          .getModificationStartRegCart(getState()) || regCart;
        pricingInfo = getPlannerOverriddenPricingInfo(pricingInfo, regCartPayment,
          regCartPricing, currentEventRegistrationId, isOverrideFeesValid, isOverrideRefundsValid,
          correctRegCart);
      }
      LOG.debug('calculatePricing', pricingInfo);
      if (regCart.status === 'INPROGRESS' && regCart.paymentInfo
        && regCart.paymentInfo.paymentStatus === 'PaymentFailed') {
        // for third party payment failed cases, do not update pricing info
        return;
      }
      const response = await regCartClient.calculateRegCartPricing(accessToken, regCart.regCartId, pricingInfo);
      LOG.debug('calculatePricing success', response);
      dispatch({ type: CALCULATE_PRICING_SUCCESS, payload: {
        regCartPricing: response.regCartPricing,
        validationMessages: response.validationMessages
      } });
    } catch (error) {
      LOG.info('calculatePricing failed', error);
      if (isConflict(error)) {
        await dispatch(promptToTakeOverRegistration());
      } else {
        dispatch({ type: CALCULATE_PRICING_FAILURE, payload: { error } });
        throw error;
      }
    }
  };
}

/**
 * Format year and month in the format YYYY-MM
 */
function formatExpirationDate(expirationYear, expirationMonth) {
  const zeros = '0000';
  const year = expirationYear.toString();
  const month = expirationMonth.toString();
  return `${zeros.slice(0, 4 - year.length)}${year}-${zeros.slice(0, 2 - month.length)}${month}`;
}

export function getRegCartPricingInfoForCheckout(pricingInfo) {
  const { paymentMethodType, paymentDetail } = pricingInfo;
  let creditCard = paymentDetail && paymentDetail.creditCard || {};
  const { expirationMonth, expirationYear } = creditCard;
  if (paymentDetail) {
    creditCard = {
      ...creditCard,
      // Format date for payment service YYYY-MM
      expirationDate: formatExpirationDate(expirationYear, expirationMonth),
      type: paymentMethodType
    };
  } else {
    creditCard = null;
  }

  switch (pricingInfo.paymentType) {
    case 'Online': {
      return {
        ...pricingInfo,
        paymentDetail: {
          ...paymentDetail,
          creditCard,
          paymentMethodMode: 'UseNewPaymentMethod',
          browserLandingURL: window.location.href
        }
      };
    }
    case 'Offline': {
      return {
        ...pricingInfo,
        paymentDetail: {}
      };
    }
    default: {
      return pricingInfo;
    }
  }
}

function isConflict(error) {
  return (error instanceof ServiceError && error.responseStatus && error.responseStatus === 409);
}
